import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';

import authApis from '../../../APIs/auth';
import lock from '../../../assets/icons/Lock.svg';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { useRoot } from '../../../context/RootProvider';
import {
  Button,
  FormContainer,
  Input,
  Typography,
} from '../../../designComponents';
import { requestAPI } from '../../../utils';
import { generatePassword } from '../../../utils/generateRandomPassword';
import { ChangePasswordStyled } from './style';
import userDataValidation from './validation';

interface initialValuesTypes {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const ChangePassword = () => {
  const theme = useTheme();
  const formikRef = useRef<FormikProps<initialValuesTypes>>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { clearAuth } = useRoot();

  const [isClickedEyeButton, setIsClickedEyeButton] = useState(false);
  const [initialValues, _setInitialValues] = useState<initialValuesTypes>({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const handleGenerateRandomPassword = () => {
    const password = generatePassword();
    if (formikRef.current) {
      formikRef.current.setFieldValue('newPassword', password);
      formikRef.current.setFieldValue('confirmNewPassword', password);
    }
  };
  const handleChangePassword = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => {
    try {
      setIsLoading(true);
      await requestAPI(
        authApis.changePassword({
          currentPassword: values.oldPassword,
          newPassword: values.newPassword,
        }),
      );
      toast.success('Password changed successfully.');
      formikRef.current?.resetForm();
      clearAuth();
      navigate('/login');
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);
      toast.error(error);
    } finally {
      setShowModal(false);
    }
  };
  return (
    <ChangePasswordStyled>
      <div className="heading">
        <Typography
          className="setting-title"
          size={14}
          strong
          style={{ marginBottom: '10px' }}
        >
          Change Password
        </Typography>
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleChangePassword}
        enableReinitialize
        validationSchema={userDataValidation.User}
        innerRef={formikRef}
      >
        {({ values, setFieldValue, handleSubmit, touched, errors }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FormContainer
                label="Old Password"
                error={touched?.oldPassword ? errors.oldPassword : ''}
                marginBottom="20px"
              >
                <Input
                  type={isClickedEyeButton ? 'text' : 'password'}
                  name="oldPassword"
                  value={values.oldPassword || ''}
                  onChange={(e) => setFieldValue('oldPassword', e.target.value)}
                  placeholder="Enter old password"
                  iconLeft={<img src={lock} alt="lock icon" />}
                  iconRight={
                    <div
                      role="button"
                      onClick={() => setIsClickedEyeButton((prev) => !prev)}
                    >
                      {isClickedEyeButton ? (
                        <IoEyeOutline size={18} />
                      ) : (
                        <IoEyeOffOutline size={18} />
                      )}
                    </div>
                  }
                  autoComplete="off"
                />
              </FormContainer>
              <FormContainer
                label="New Password"
                error={touched?.newPassword ? errors.newPassword : ''}
                marginBottom="20px"
              >
                <Input
                  type={isClickedEyeButton ? 'text' : 'password'}
                  name="newPassword"
                  value={values.newPassword}
                  onChange={(e) => setFieldValue('newPassword', e.target.value)}
                  placeholder="Enter new password"
                  iconLeft={<img src={lock} alt="lock icon" />}
                  iconRight={
                    <div
                      role="button"
                      onClick={() => setIsClickedEyeButton((prev) => !prev)}
                    >
                      {isClickedEyeButton ? (
                        <IoEyeOutline size={18} />
                      ) : (
                        <IoEyeOffOutline size={18} />
                      )}
                    </div>
                  }
                  autoComplete="off"
                />
              </FormContainer>
              <Button
                onClick={handleGenerateRandomPassword}
                variant="secondary"
                bgColor={theme.colors.darkSlateGray.faded}
                style={{ marginBottom: '20px' }}
              >
                Generate New Password
              </Button>
              <FormContainer
                label="Confirm New Password"
                error={
                  touched?.confirmNewPassword ? errors.confirmNewPassword : ''
                }
                marginBottom="20px"
              >
                <Input
                  type={isClickedEyeButton ? 'text' : 'password'}
                  name="confirmNewPassword"
                  value={values.confirmNewPassword}
                  onChange={(e) =>
                    setFieldValue('confirmNewPassword', e.target.value)
                  }
                  placeholder="Enter confirm new password"
                  iconLeft={<img src={lock} alt="lock icon" />}
                  iconRight={
                    <div
                      role="button"
                      onClick={() => setIsClickedEyeButton((prev) => !prev)}
                    >
                      {isClickedEyeButton ? (
                        <IoEyeOutline size={18} />
                      ) : (
                        <IoEyeOffOutline size={18} />
                      )}
                    </div>
                  }
                  autoComplete="off"
                />
              </FormContainer>
              <div className="btn-update">
                <>
                  <Button
                    variant="secondary"
                    style={{ marginRight: '18px' }}
                    // type="submit"
                    onClick={async () => {
                      if (formikRef.current) {
                        await formikRef.current.validateForm();
                        formikRef.current.setTouched({
                          confirmNewPassword: true,
                          newPassword: true,
                          oldPassword: true,
                        });
                        if (Object.keys(formikRef.current.errors).length > 0) {
                          return;
                        }
                        setShowModal(true);
                      }
                    }}
                    isLoading={isLoading}
                  >
                    Change Password
                  </Button>
                </>
              </div>
            </Form>
          );
        }}
      </Formik>
      {showModal ? (
        <ConfirmationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          message="Are you sure want to change the password"
          title="Change Password"
          onOk={() => formikRef.current?.handleSubmit()}
          loading={isLoading}
        />
      ) : (
        ''
      )}
    </ChangePasswordStyled>
  );
};

export default ChangePassword;
